import React, { useEffect, useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { injectIntl } from "react-intl";
import Logo from "../../assests/images/login-logo.png";
import SingleInput from "./SingleOTPInput";
import { isEmpty } from "lodash";
import {
  setCounterInLocalStorage,
  setUserInLocalStorage,
  getCounterFromLocalStorage,
  getUserFromLocalStorage,
  logout,
} from "../../userStore";

const otpLength = 4;

const confirmCodeInitialValues = {
  code1: "",
  code2: "",
  code3: "",
  code4: "",
};

function OTP(props) {
  useEffect(() => {
    try {
      document.title = `ethizo`;
      if (window?.NUSA_closeVuiForm) {
        window?.NUSA_closeVuiForm();
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  const [activeInput, setActiveInput] = useState(1);
  const [user, setUser] = useState(null);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    const storedUser = getUserFromLocalStorage();
    const storedCounter = getCounterFromLocalStorage();
    if (storedUser) setUser(storedUser);
    if (storedCounter) setCounter(Number(storedCounter));
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: confirmCodeInitialValues,
  });

  const onSubmit = (values) => {
    const { code1, code2, code3, code4 } = values;

    const code = `${code1}${code2}${code3}${code4}`;
    const newCounter = counter + 1;
    setCounterInLocalStorage(newCounter);
    setCounter(counter);
    if (code === user?.pincode) {
      props.handleReset();
    } else {
      reset(confirmCodeInitialValues);
      setError("code", { type: "manual", message: "Your PIN is not valid" });
    }
  };

  const focusInput = useCallback(
    (inputIndex) => {
      const selectedIndex = Math.max(Math.min(otpLength, inputIndex), 0);
      setActiveInput(selectedIndex);
    },
    [otpLength]
  );

  const handleOnFocus = useCallback(
    (index) => () => {
      focusInput(index);
    },
    [focusInput]
  );

  const focusPrevInput = useCallback(() => {
    if (activeInput > 1) {
      focusInput(activeInput - 1);
    }
  }, [activeInput, focusInput]);

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  useEffect(() => {
    if (isSubmitted && isEmpty(errors)) {
      setTimeout(() => {
        reset(confirmCodeInitialValues);
        setActiveInput(1);
      }, 3000);
    }
  }, [isSubmitted, errors, reset]);

  useEffect(() => {
    if (props?.isIdle && isEmpty(errors)) {
      reset(confirmCodeInitialValues);
    }
  }, [props?.isIdle, reset]);

  const handleOnChange = (value, index) => {
    if (errors.code) {
      clearErrors("code");
    }
    if (isNaN(value)) return;
    setValue(`code${index}`, value);
    if (index !== 4) {
      focusNextInput();
    }
  };

  const onBlur = useCallback(() => {
    setActiveInput(-1);
  }, []);

  const handleOnKeyDown = useCallback(
    (e) => {
      const pressedKey = e.key;
      if (errors.code) {
        clearErrors("code");
      }
      switch (pressedKey) {
        case "Backspace":
        case "Delete": {
          e.preventDefault();
          if (e.target.value) {
            setValue(`code${activeInput}`, "");
          } else {
            focusPrevInput();
          }
          focusPrevInput();
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case "ArrowRight": {
          e.preventDefault();
          focusNextInput();
          break;
        }
        default: {
          if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
            e.preventDefault();
          }
          break;
        }
      }
    },
    [
      activeInput,
      focusNextInput,
      focusPrevInput,
      clearErrors,
      setValue,
      errors.code,
    ]
  );

  const handleOnPaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text/plain")
        .trim()
        .slice(0, otpLength + 1 - activeInput)
        .split("");
      if (pastedData) {
        let nextFocusIndex = 1;
        Object.values(confirmCodeInitialValues).forEach((val, index) => {
          if (index + 1 >= activeInput) {
            const changedValue = pastedData.shift() || val;
            if (!isNaN(changedValue)) {
              nextFocusIndex = index + 1;
              setValue(`code${nextFocusIndex}`, changedValue);
            }
          }
        });
        setActiveInput(Math.min(nextFocusIndex, otpLength));
      }
    },
    [activeInput, otpLength, setValue]
  );

  useEffect(() => {
    const { code1, code2, code3, code4 } = control._formValues;
    if (code1 !== "" && code2 !== "" && code3 !== "" && code4 !== "") {
      setTimeout(() => {
        handleSubmit(onSubmit)();
      }, 100);
    }
  }, [control._formValues, handleSubmit, onSubmit]);

  return (
    <div className="login-bg h-screen flex items-center justify-center z-[100]">
      <div className="login-box rounded-3xl p-12 w-[650px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-center">
            <div>
              <h3 className="text-lg mb-2 font-normal text-black-39">
                Welcome to
              </h3>
              <img src={Logo} alt="" className="w-[237px]" />
              <p className="mt-2 text-right text-vColor font-light">V 2.0</p>
            </div>
          </div>
          <div className="flex spacing-y-4 content-center flex-col">
            <h4 className="text-3xl text-center text-black font-light mb-4">
              Please Enter 4 Digits PIN
            </h4>
            <div className="self-center flex flex-row justify-between w-4/5 mb-4">
              {Array(otpLength)
                .fill("")
                .map((_, index) => {
                  const curIndex = index + 1;
                  return (
                    <Controller
                      key={curIndex.toString()}
                      name={`code${curIndex}`}
                      control={control}
                      render={({ field }) => (
                        <SingleInput
                          {...field}
                          type="password"
                          value={field.value || ""}
                          className={`w-20 h-20 font-black text-2xl text-center border bg-white focus-within:bg-white outlineStyle rounded ${
                            errors.code
                              ? "border-red-900 focus-within:border-red-900"
                              : "border-gray focus:border-teal-c-900 focus-within:border-teal-c-900"
                          }`}
                          maxLength="1"
                          focus={activeInput === curIndex}
                          autoFocus
                          disabled={counter > 2}
                          onFocus={handleOnFocus(curIndex)}
                          onChange={(e) =>
                            handleOnChange(e.target.value, curIndex)
                          }
                          onKeyDown={handleOnKeyDown}
                          onBlur={onBlur}
                          onPaste={handleOnPaste}
                        />
                      )}
                    />
                  );
                })}
            </div>
            <div
              style={{
                background:
                  errors.code || counter > 2 ? "rgb(235 16 16 / 10%)" : "",
              }}
              className="self-center h-12 flex justify-center items-center w-4/5 text-red-900 sm:text-sm text-xs truncate">
              <p>
                {errors.code?.message || " "}{" "}
                {counter > 2
                  ? "Your maximum attempts reached for PIN verification please logout"
                  : ""}
              </p>
            </div>

            <div className="mt-4 self-center gap-4 flex flex-row justify-between w-4/5">
              <button
                type="button"
                className="flex items-center justify-center w-full rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm  hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600  bg-indigo-600"
                onClick={() => {
                  logout();
                  props?.handleReset();
                }}>
                Logout
              </button>
              <button
                type="submit"
                className="flex items-center justify-center w-full rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm  hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600  bg-indigo-600"
                onClick={handleSubmit(onSubmit)}>
                Login
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OTP;
